import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Label } from '@/components/ui/label';
import {
  divisionListQueryOptions,
  districtListQueryOptions,
  thanaListQueryOptions,
} from '@/features/geo/api/geo.api';
import { cn } from '@/lib/utils';
import { OptionSelect } from './OptionSelect';

interface GeoUnitSelectProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  disabled?: boolean;
  id?: string;
  divisionId?: number | null | undefined;
  districtId?: number | null | undefined;
  required?: boolean;
  error?: string | undefined;
  className?: string | undefined;
}

export function GeoUnitSelect({
  value,
  onChange,
  disabled,
  id,
  divisionId: controlledDivisionId,
  districtId: controlledDistrictId,
  required,
  error,
  className,
}: GeoUnitSelectProps) {
  const [divisionId, setDivisionId] = useState<number | undefined>(
    controlledDivisionId ?? undefined,
  );
  const [districtId, setDistrictId] = useState<number | undefined>(
    controlledDistrictId ?? undefined,
  );

  const [syncedDivisionId, setSyncedDivisionId] = useState(controlledDivisionId);
  const [syncedDistrictId, setSyncedDistrictId] = useState(controlledDistrictId);

  if (controlledDivisionId !== syncedDivisionId) {
    setSyncedDivisionId(controlledDivisionId);
    setDivisionId(controlledDivisionId ?? undefined);
  }

  if (controlledDistrictId !== syncedDistrictId) {
    setSyncedDistrictId(controlledDistrictId);
    setDistrictId(controlledDistrictId ?? undefined);
  }

  const { data: divisions, isLoading: divisionsLoading } = useQuery(divisionListQueryOptions());
  const { data: districts, isLoading: districtsLoading } = useQuery({
    ...districtListQueryOptions(divisionId ?? 0),
    enabled: divisionId !== undefined,
  });
  const { data: thanas, isLoading: thanasLoading } = useQuery({
    ...thanaListQueryOptions(districtId ?? 0),
    enabled: districtId !== undefined,
  });

  function handleDivisionChange(nextDivisionId: number | undefined) {
    setDivisionId(nextDivisionId);
    setDistrictId(undefined);
    onChange(undefined);
  }

  function handleDistrictChange(nextDistrictId: number | undefined) {
    setDistrictId(nextDistrictId);
    onChange(undefined);
  }

  function handleThanaChange(nextThanaId: number | undefined) {
    onChange(nextThanaId);
  }

  const isDistrictDisabled = disabled || divisionId === undefined;
  const isThanaDisabled = disabled || districtId === undefined;

  const baseId = id ?? 'geo';
  const divisionFieldId = `${baseId}-division`;
  const districtFieldId = `${baseId}-district`;
  const thanaFieldId = `${baseId}-thana`;

  const requiredMark = required ? <span className="text-destructive">*</span> : null;

  return (
    <div
      role="group"
      aria-label="Geographic unit"
      className={cn('grid grid-cols-1 sm:grid-cols-3 gap-3', className)}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={divisionFieldId} className="gap-0.5">
          Division{requiredMark}
        </Label>
        <OptionSelect
          id={divisionFieldId}
          options={divisions}
          isLoading={divisionsLoading}
          value={divisionId}
          onChange={handleDivisionChange}
          placeholder="Select division"
          disabled={disabled ?? false}
          clearable
          aria-invalid={Boolean(required && error && divisionId === undefined)}
        />
        {required && error && divisionId === undefined && (
          <p className="text-xs text-destructive">Division is required</p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={districtFieldId} className="gap-0.5">
          District{requiredMark}
        </Label>
        <OptionSelect
          id={districtFieldId}
          options={districts}
          isLoading={districtsLoading}
          value={districtId}
          onChange={handleDistrictChange}
          placeholder={divisionId === undefined ? 'Select division first' : 'Select district'}
          disabled={isDistrictDisabled}
          clearable
          aria-invalid={Boolean(required && error && districtId === undefined)}
        />
        {required && error && districtId === undefined && (
          <p className="text-xs text-destructive">District is required</p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={thanaFieldId} className="gap-0.5">
          Thana{requiredMark}
        </Label>
        <OptionSelect
          id={thanaFieldId}
          options={thanas}
          isLoading={thanasLoading}
          value={value}
          onChange={handleThanaChange}
          placeholder={districtId === undefined ? 'Select district first' : 'Select thana'}
          clearable
          disabled={isThanaDisabled}
          aria-invalid={Boolean(required && error && value === undefined)}
        />
        {required && error && value === undefined && (
          <p className="text-xs text-destructive">Thana is required</p>
        )}
      </div>
    </div>
  );
}
